import * as THREE from "three";
import { heatStatus, type CoffeeCup, type Heated } from "../game/coffee";

// The coffee truck's things you hold and make: a portafilter with its dose
// of grounds, a shot glass filling with espresso, a steel pitcher of milk,
// and a paper cup showing what's in it (dark with only espresso, tan once
// there's milk, a cap of foam on top).

const steelMat = () => new THREE.MeshStandardMaterial({ color: 0xc9d0d6, metalness: 0.75, roughness: 0.3 });
const ESPRESSO = 0x3b1d0e;
const BITTER = 0x1a0c05;
const SOUR = 0x8a5a2a;

/** A shot's colour: amber when it's pulled short, dark when it ran long. */
function shotColor(h: Heated): number {
  const s = heatStatus(h).status;
  return s === "cooking" ? SOUR : s === "burnt" ? BITTER : ESPRESSO;
}

export class PortafilterModel {
  readonly group = new THREE.Group();
  private readonly grounds: THREE.Mesh;

  constructor() {
    const basket = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.03, 0.03, 20, 1, true), steelMat());
    basket.material.side = THREE.DoubleSide;
    const bottom = new THREE.Mesh(new THREE.CircleGeometry(0.03, 20), steelMat());
    bottom.rotation.x = -Math.PI / 2;
    bottom.position.y = -0.015;
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.014, 0.12, 10), new THREE.MeshStandardMaterial({ color: 0x1d1d1f, roughness: 0.5 }));
    handle.rotation.z = Math.PI / 2;
    handle.position.x = 0.09;
    this.grounds = new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.03, 1, 20), new THREE.MeshStandardMaterial({ color: 0x4a2c17, roughness: 0.95 }));
    this.group.add(basket, bottom, handle, this.grounds);
  }

  /** How full of grounds, 0 empty to 1 at the rim; over it, a heap spilling over. */
  set(dose: number): void {
    const h = Math.max(0.001, Math.min(dose, 1.15)) * 0.028;
    this.grounds.scale.set(dose > 1 ? 1.1 : 1, h, dose > 1 ? 1.1 : 1);
    this.grounds.position.y = -0.015 + h / 2;
    this.grounds.visible = dose > 0;
  }
}

/** A shot glass with espresso rising in it as it pulls. */
export class ShotModel {
  readonly group = new THREE.Group();
  private readonly coffee: THREE.Mesh;
  private readonly crema: THREE.Mesh;
  private readonly mat = new THREE.MeshStandardMaterial({ color: ESPRESSO, roughness: 0.4 });

  constructor() {
    const glass = new THREE.Mesh(
      new THREE.LatheGeometry([new THREE.Vector2(0, 0), new THREE.Vector2(0.022, 0), new THREE.Vector2(0.026, 0.07), new THREE.Vector2(0.024, 0.07), new THREE.Vector2(0.02, 0.006), new THREE.Vector2(0, 0.006)], 20),
      new THREE.MeshStandardMaterial({ color: 0xe8f1f4, transparent: true, opacity: 0.3, roughness: 0.1, depthWrite: false }),
    );
    glass.renderOrder = 2;
    this.coffee = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.018, 1, 16), this.mat);
    this.coffee.renderOrder = 1;
    this.crema = new THREE.Mesh(new THREE.CircleGeometry(0.02, 16), new THREE.MeshStandardMaterial({ color: 0xc08a4e, roughness: 0.6 }));
    this.crema.rotation.x = -Math.PI / 2;
    this.group.add(glass, this.coffee, this.crema);
  }

  set(h: Heated): void {
    const level = Math.max(0.002, Math.min(1, h.cook)) * 0.05;
    this.coffee.scale.y = level;
    this.coffee.position.y = 0.006 + level / 2;
    this.crema.position.y = 0.007 + level;
    this.mat.color.setHex(shotColor(h));
  }
}

/** A steel milk pitcher: the milk in it goes from cold white to steamed, with foam. */
export class PitcherModel {
  readonly group = new THREE.Group();
  private readonly milk: THREE.Mesh;
  private readonly milkMat = new THREE.MeshStandardMaterial({ color: 0xf6f4ee, roughness: 0.7 });

  constructor() {
    const body = new THREE.Mesh(
      new THREE.LatheGeometry([new THREE.Vector2(0, 0), new THREE.Vector2(0.042, 0), new THREE.Vector2(0.045, 0.06), new THREE.Vector2(0.036, 0.11), new THREE.Vector2(0.033, 0.11), new THREE.Vector2(0.041, 0.06), new THREE.Vector2(0.038, 0.004), new THREE.Vector2(0, 0.004)], 24),
      steelMat(),
    );
    body.material.side = THREE.DoubleSide;
    const handle = new THREE.Mesh(new THREE.TorusGeometry(0.028, 0.006, 6, 12, Math.PI), steelMat());
    handle.rotation.z = -Math.PI / 2;
    handle.position.set(0.044, 0.06, 0);
    this.milk = new THREE.Mesh(new THREE.CircleGeometry(0.037, 20), this.milkMat);
    this.milk.rotation.x = -Math.PI / 2;
    this.milk.position.y = 0.075;
    this.group.add(body, handle, this.milk);
  }

  set(h: Heated): void {
    // Steamed milk rises with foam; scalded milk browns a little.
    const s = heatStatus(h);
    this.milk.position.y = 0.07 + Math.min(1, h.cook) * 0.02;
    this.milkMat.color.setHex(s.status === "burnt" ? 0xe8d8b8 : 0xf6f4ee);
  }
}

/** A paper cup with a brown sleeve, and what's in it. */
export class PaperCupModel {
  readonly group = new THREE.Group();
  private readonly fill: THREE.Mesh;
  private readonly foam: THREE.Mesh;
  private readonly fillMat = new THREE.MeshStandardMaterial({ color: ESPRESSO, roughness: 0.5 });

  constructor() {
    const cup = new THREE.Mesh(
      new THREE.LatheGeometry([new THREE.Vector2(0, 0), new THREE.Vector2(0.03, 0), new THREE.Vector2(0.042, 0.12), new THREE.Vector2(0.044, 0.122), new THREE.Vector2(0.04, 0.12), new THREE.Vector2(0.028, 0.004), new THREE.Vector2(0, 0.004)], 24),
      new THREE.MeshStandardMaterial({ color: 0xf6f2ea, roughness: 0.8, side: THREE.DoubleSide }),
    );
    const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(0.0405, 0.0355, 0.045, 24, 1, true), new THREE.MeshStandardMaterial({ color: 0x8b5a2b, roughness: 0.9 }));
    sleeve.position.y = 0.058;
    this.fill = new THREE.Mesh(new THREE.CircleGeometry(1, 20), this.fillMat);
    this.fill.rotation.x = -Math.PI / 2;
    this.foam = new THREE.Mesh(new THREE.CircleGeometry(1, 20), new THREE.MeshStandardMaterial({ color: 0xf2e6d2, roughness: 0.9 }));
    this.foam.rotation.x = -Math.PI / 2;
    this.group.add(cup, sleeve, this.fill, this.foam);
  }

  set(c: CoffeeCup): void {
    // Each shot is a little; milk fills it most of the way.
    const level = Math.min(0.115, c.shots.length * 0.018 + (c.milk ? 0.075 : 0));
    const r = 0.03 + (0.04 - 0.03) * (level / 0.12);
    this.fill.visible = level > 0;
    this.fill.position.y = level;
    this.fill.scale.setScalar(r);
    this.fillMat.color.setHex(c.milk ? 0xb88a5c : c.shots[0] ? shotColor(c.shots[0]) : ESPRESSO);
    this.foam.visible = !!c.milk;
    this.foam.position.y = level + 0.001;
    this.foam.scale.setScalar(r * 0.7);
  }
}
